import {createSlice} from '@reduxjs/toolkit'
import {resetSearchState} from 'app/features/orders/redux/product/searchProductSlice'
import * as model from 'app/features/orders/models'
import draftOrderService from 'app/services/DraftOrderService'
import {deliveryTypes} from 'app/constant'
import {deleteDraftOrderShipping, updateDraftOrder} from '../../services/draftOrderService'
import {setIsComplianceChecked} from './shippingSlice'
import {hideLoader, showLoader} from 'app/redux/loaderSlice'
import {showMessage} from 'app/redux/messageSlice'
import draftOrderPaymentService from 'app/services/DraftOrderPaymentService/DraftOrderPaymentService'
import {DraftOrderState, draftOrderInitialState, orderStatus} from '../../models/DraftOrder'
import {mapToDiscountModel, mapToDraftOrderModel} from './helper'
import * as actions from './actions'
import {getDraftOrder} from './actions'
import {RootState} from 'setup'

export const draftOrderFetched = (order: any) => async (dispatch: any) => {
  const draftOrder: any = mapToDraftOrderModel(order)
  const discounts = mapToDiscountModel(
    order.discounts.nodes,
    draftOrder.totalPrice,
    draftOrder.status === orderStatus.completed
  )
  dispatch(setViewDiscounts(discounts))
  dispatch(setDraftOrder(draftOrder))
  dispatch(setCustomer(order.customer))
  dispatch(
    setFetchedAddress({
      shippingAddress: order.shippingAddress,
      billingAddress: order.billingAddress,
      paymentMethod: order.paymentMethod,
    })
  )
}

export const orderDeliveryMethodChanged = (order: any) => async (dispatch: any, getState: any) => {
  try {
    dispatch(showLoader())
    const state: any = getState()
    const {discounts}: DraftOrderState = state.orders.draft

    if (order.orderDeliveryMethod === deliveryTypes.pickup) {
      if (discounts && discounts.shippingDiscountCodeId) {
        await draftOrderService.removeDraftOrderDiscount(discounts.shippingDiscountCodeId, order.id)
      }
      if (order.shippingRateId) {
        await deleteDraftOrderShipping(order)
      }
    }

    dispatch(setOrderDeliveryMethod(order.orderDeliveryMethod))
    dispatch(setIsComplianceChecked(false))
  } catch (error) {
    console.error(error)
    dispatch(showMessage({text: 'Error occurred while changing delivery method', variant: 'error'}))
  } finally {
    dispatch(hideLoader())
  }
}

export const changeDraftOrderBilling =
  (address: any, paymentMethodId: string) => async (dispatch: any) => {
    const order: model.DraftOrder = await dispatch(getDraftOrder())
    try {
      if (paymentMethodId && !order.id) {
        const res = await draftOrderPaymentService.paymentMethodUpdate({
          id: paymentMethodId,
          addressId: address.id,
        })
        if (res && res.status === 'success') {
          dispatch(
            showMessage({
              text: 'Customer Payment billing address has been successfully ',
              variant: 'success',
            })
          )
        } else {
          dispatch(
            showMessage({
              text: 'An error occurred while saving billing address',
              variant: 'error',
            })
          )
        }
      }
      if (paymentMethodId && order.id) {
        await draftOrderService.updateDraftOrderPaymentBillingMethod({
          id: order.id,
          paymentMethodId,
          billingAddressId: address.id,
        })
        dispatch(
          showMessage({
            text: 'Customer Payment billing address has been successfully ',
            variant: 'success',
          })
        )
      }
      dispatch(setChangeBillingAddress(address))
    } catch (error) {
      console.log(error)
      dispatch(
        showMessage({
          text: 'An error occurred while saving billing address',
          variant: 'error',
        })
      )
    }
    return true
  }

export const applyWineClubDiscount = (model: any) => async (dispatch: any) => {
  const discount = await draftOrderService.applyWineClubDiscountCode(model)
  dispatch(findTypeOfDiscount(discount))
}

export const findTypeOfDiscount = (discount: any) => async (dispatch: any) => {
  const order: model.DraftOrder = await dispatch(getDraftOrder())
  const discounts = mapToDiscountModel(discount.discounts, order.totalPrice)
  if (discount.shippingRate) {
    dispatch(updateShippingPrice(discount.shippingRate))
  }
  dispatch(setDiscounts(discounts))
  dispatch(setWineClubDiscount({discountAmount: discounts.wineClubDiscounts}))
  dispatch(setDiscount({discountAmount: discounts.totalDiscounts, id: discounts.discountCodeId}))
  dispatch(setFreeShipping(discounts.isFreeShipping))
  dispatch(setIsComplianceChecked(false))
}

export const checkValidPaymentMethod =
  ({paymentMethod, id}: any) =>
  async (dispatch: any) => {
    if (paymentMethod?.id) {
      const isValid = await draftOrderPaymentService.validatePaymentMethod(id, paymentMethod.id)
      if (!isValid) {
        dispatch(setErrors(['Payment method is not valid']))
        dispatch(resetPaymentState())
      }
    }
  }

export const selectDraftOrderCustomer = (customer: any) => async (dispatch: any) => {
  const draftOrder = await dispatch(getDraftOrder())
  await dispatch(checkValidPaymentMethod(customer))
  await dispatch(setCustomer(customer))
  if (draftOrder && draftOrder.id) {
    await dispatch(
      applyWineClubDiscount({
        draftOrderId: draftOrder.id,
        customerId: customer.id,
      })
    )
  }
  dispatch(setIsComplianceChecked(false))
}

export const validateDiscounts = () => async (dispatch: any) => {
  const draftOrder = await dispatch(getDraftOrder())
  if (draftOrder.id && draftOrder.customerId) {
    await updateDraftOrder(draftOrder, draftOrder.id)
    dispatch(validateDiscount({draftOrderId: draftOrder.id, customerId: draftOrder.customerId}))
  }
}

export const validateDiscount = (model: any) => async (dispatch: any) => {
  const discount = await draftOrderService.validateDiscount(model)
  dispatch(findTypeOfDiscount(discount))
}

export const removeCustomerDiscounts = () => async (dispatch: any) => {
  const draftOrder = await dispatch(getDraftOrder())
  if (draftOrder.id) {
    const {totalPrice, subtotalPrice} = draftOrder
    await draftOrderService.removeDraftOrderDiscounts({
      draftOrderId: draftOrder.id,
      pTotalPrice: totalPrice,
      pSubtotalPrice: subtotalPrice,
    })
  }
}

export const resetDraftOrderState = () => async (dispatch: any) => {
  dispatch(resetSearchState())
  dispatch(resetState())
}

const draftOrderSlice = createSlice({
  name: 'draftOrder',
  initialState: draftOrderInitialState,
  reducers: {
    setDraftOrder: (state, action: any) => actions.setDraftOrder(state, action),
    setDraftOrderId: (state, action) => actions.setDraftOrderId(state, action),
    setDiscounts: (state, action) => actions.setDiscounts(state, action),
    setOrderDeliveryMethod: (state, action) => actions.setOrderDeliveryMethod(state, action),
    setShippingRate: (state, action) => actions.setShippingRate(state, action),
    updateShippingPrice: (state, action) => actions.updateShippingPrice(state, action),
    setShippingComplianceAndTax: (state, action) =>
      actions.setShippingComplianceAndTax(state, action),
    addOrderItems: (state, action) => actions.addOrderItems(state, action),
    setQuantity: (state, action) => actions.setQuantity(state, action),
    removeOrderItem: (state, action) => actions.removeOrderItem(state, action),
    setCustomer: (state, action) => actions.setCustomer(state, action),
    setFetchedAddress: (state, action) => actions.setFetchedAddress(state, action),
    setChangeShippingAddress: (state, action) => actions.setChangeShippingAddress(state, action),
    setChangeBillingAddress: (state, action) => actions.setChangeBillingAddress(state, action),
    setPaymentMethod: (state, action) => actions.setStatePaymentMethod(state, action.payload),
    setPaymentMethodId: (state, action) => actions.setStatePaymentMethod(state, action),
    setFreeShipping: (state, action) => actions.setFreeShipping(state, action),
    setDiscount: (state, action) => actions.setDiscount(state, action),
    setWineClubDiscount: (state, action) => actions.setWineClubDiscount(state, action),
    setDirty: (state, action) => actions.setDirty(state, action),
    setOrderNote: (state, action) => actions.setOrderNote(state, action),
    setErrors: (state, action) => actions.setErrors(state, action),
    setValid: (state, action) => actions.setValid(state, action),
    setIsEditMode: (state, action) => actions.setIsEditMode(state, action),
    setViewDiscounts: (state, action) => actions.setViewDiscounts(state, action),
    setPaymentType: (state, action) => actions.setPaymentType(state, action),
    setIsAutoDiscountRemoved: (state, action) => actions.setIsAutoDiscountRemoved(state, action),
    setIsTaxRemoved: (state, action) => actions.setIsTaxRemoved(state, action),
    resetShippingTax: (state) => actions.resetShippingTax(state),
    resetShippingDiscount: (state) => actions.resetShippingDiscount(state),
    resetPaymentState: (state) => actions.resetPaymentState(state),
    removeCustomer: (state) => actions.removeCustomer(state),
    resetState: () => {
      return draftOrderInitialState
    },
  },
})

export const {
  addOrderItems,
  setQuantity,
  setCustomer,
  removeCustomer,
  removeOrderItem,
  setDraftOrder,
  setDraftOrderId,
  resetState,
  setShippingRate,
  setDiscount,
  setShippingComplianceAndTax,
  setDirty,
  setPaymentMethodId,
  setChangeShippingAddress,
  setChangeBillingAddress,
  setFetchedAddress,
  setOrderDeliveryMethod,
  setWineClubDiscount,
  setErrors,
  setValid,
  setPaymentMethod,
  resetPaymentState,
  setFreeShipping,
  setDiscounts,
  setOrderNote,
  setIsEditMode,
  setViewDiscounts,
  setPaymentType,
  setIsAutoDiscountRemoved,
  resetShippingTax,
  setIsTaxRemoved,
  updateShippingPrice,
  resetShippingDiscount,
} = draftOrderSlice.actions

export default draftOrderSlice.reducer

export const selectIsEditMode = ({orders}: RootState): boolean => orders.draft.isEditMode
export const selectViewDraftOrderDiscounts = ({orders}: RootState): boolean =>
  orders.draft.viewDiscounts
export const selectCustomer = ({orders}: RootState): any => orders.draft.customer
export const selectDraftOrder = ({orders}: RootState): model.DraftOrder => orders.draft.order
export const selectIsDirty = ({orders}: RootState): boolean => orders.draft.isDirty
export const selectDraftOrderDiscounts = ({orders}: RootState): any => orders.draft.discounts
export const selectDraftOrderIsValid = ({orders}: RootState): boolean => orders.draft.isValid
export const selectDraftPaymentType = ({orders}: RootState): string | any =>
  orders.draft.order.paymentType
export const selectIsAutoDiscountRemoved = ({orders}: RootState): boolean =>
  orders.draft.order.isAutoDiscountRemoved

export const selectIsTaxRemoved = ({orders}: RootState): boolean => orders.draft.order.isTaxRemoved

export const selectIsTaxFetching = ({orders}: RootState): boolean => orders.shipping.isTaxFetching
